/**
 * Quotes and contracts (phase 1b, MB decision 128): the entities of `docs/specs/quotes/overview.md`, mirrored one to
 * one by the Postgres tables of `supabase/migrations/20261018001600_quotes_contracts.sql`. Sources: TJR inventory 01
 * (`docs/reference/tjr-inventory/01-quote-contract-setup.md` §2–§4, §10.2) and MB's decisions 118–127.
 *
 * Money is kept in the database as `numeric(12,2)` and here as a number of dollars; every sum is done in whole cents
 * (`quote-calc.ts`), so prices are exact to the cent.
 */
import type { IsoDate, IsoDateTime, Uuid } from './model.js';
import type { QcRule } from './qc-spec.js';

export type JobType = 'fixed_wing' | 'helicopter';
export type Currency = 'AUD' | 'USD' | 'NZD';
export type Hemisphere = 'N' | 'S';

/** TJR's three denominations (`denomCB`), with the "Unites States" typo of TJR fixed (bug B11). */
export const CURRENCY_LABEL: Readonly<Record<Currency, { readonly name: string; readonly symbol: string }>> =
  {
    AUD: { name: 'Australian', symbol: '$' },
    USD: { name: 'United States', symbol: 'US$' },
    NZD: { name: 'New Zealand', symbol: 'NZ$' },
  };

/** Australian states and territories (TJR `ctrdatfls.dat` 002). */
export const AU_STATES: ReadonlyArray<{ readonly name: string; readonly short: string }> = [
  { name: 'Australian Capital Territory', short: 'ACT' },
  { name: 'New South Wales', short: 'NSW' },
  { name: 'Northern Territory', short: 'NT' },
  { name: 'Queensland', short: 'QLD' },
  { name: 'South Australia', short: 'SA' },
  { name: 'Tasmania', short: 'TAS' },
  { name: 'Victoria', short: 'VIC' },
  { name: 'Western Australia', short: 'WA' },
];

// ---------------------------------------------------------------- clients

export interface ClientOrgDetails {
  readonly id: Uuid;
  readonly tenant_id: Uuid;
  /** Name as printed on the front page (TJR record 022). */
  readonly name: string;
  /** Company name in the party sentence and notices, when it differs from `name`. */
  readonly legal_name: string;
  /** ACN or ABN (TJR `cdAcnEdit`, optional). */
  readonly company_no: string;
  readonly address_line1: string;
  readonly address_line2: string;
  readonly suburb: string;
  readonly state: string;
  readonly postcode: string;
  readonly country: string;
  readonly email: string;
  readonly web: string;
  readonly deleted_at: IsoDateTime | null;
}

export type ContactRole = 'principal' | 'notices' | 'billing' | 'field';
export const CONTACT_ROLES: readonly ContactRole[] = ['principal', 'notices', 'billing', 'field'];
export const CONTACT_ROLE_LABEL: Readonly<Record<ContactRole, string>> = {
  principal: 'Principal contact',
  notices: 'Notices',
  billing: 'Billing',
  field: 'Field contact',
};

export interface ClientContact {
  readonly id: Uuid;
  readonly tenant_id: Uuid;
  readonly client_org_id: Uuid;
  readonly name: string;
  /** Title or position, e.g. "Exploration Manager" (the 2026 contract prints it; TJR had no field). */
  readonly position: string;
  readonly email: string;
  readonly phone: string;
  readonly mobile: string;
  readonly roles: readonly ContactRole[];
  readonly deleted_at: IsoDateTime | null;
}

// ---------------------------------------------------------------- quote

export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'declined' | 'superseded' | 'expired';

/** TJR `estWordCB` (record 600.28) plus the 2026 contract's "first week of". */
export type EstStartQualifier = 'in' | 'early' | 'mid' | 'late' | 'first_week' | 'q1' | 'q2' | 'q3' | 'q4';
export type DurationUnit = 'days' | 'weeks' | 'months';

/**
 * Conduct-of-survey items (TJR `conductCLB`, record 726, contract section 1). TJR's eleven ticks allowed
 * contradictions (bug B12); here fuel and permits are each one choice (`fuel_by`, `permits_by`) and the rest are
 * independent ticks. Wording of each item is the tenant's clause template (`conduct.<id>`).
 */
export const CONDUCT_ITEMS = [
  'personnel_equipment',
  'fuel_truck',
  'fuel_positioning',
  'accommodation',
  'meals',
  'transport',
  'supplies',
] as const;
export type ConductItem = (typeof CONDUCT_ITEMS)[number];
/** Who pays for aircraft fuel (TJR items 2/3). */
export type FuelBy = 'thomson' | 'client' | 'not_stated';
/** Who obtains the permits (TJR items 6/7). */
export type PermitsBy = 'thomson_all' | 'thomson_except_tasp' | 'not_stated';

export interface Quote {
  readonly id: Uuid;
  readonly tenant_id: Uuid;
  /** `Q` + 2-digit year + 3-digit sequence (MB decision 119), unique per tenant. */
  readonly number: string;
  /** The working revision: 1 until the first is sent, then one more than the last revision sent. */
  readonly revision: number;
  readonly status: QuoteStatus;
  readonly client_org_id: Uuid | null;
  readonly principal_contact_id: Uuid | null;
  readonly notices_contact_id: Uuid | null;
  readonly project_name: string;
  /** Cover "Location", e.g. "Western Australia" (TJR record 661). */
  readonly location: string;
  /** "The survey is located … in" (TJR record 670); optional (bug B4). */
  readonly located: string;
  readonly state: string;
  readonly country: string;
  readonly job_type: JobType;
  readonly quote_date: IsoDate;
  readonly valid_until: IsoDate | null;
  readonly currency: Currency;
  /** Aircraft type offered, e.g. "Cessna 208" (TJR `qtAcCB`). */
  readonly aircraft_type: string;
  /** Cruise speed of that type in knots (TJR `tjf.dat` 002), for the sample distances. */
  readonly aircraft_cruise_kt: number | null;
  /** Contract aircraft registrations (TJR record 732); empty on a quote. */
  readonly aircraft_regos: readonly string[];
  readonly bases: readonly string[];
  readonly est_start_qualifier: EstStartQualifier;
  /** YYYY-MM. */
  readonly est_start_month: string;
  readonly est_duration_value: number | null;
  readonly est_duration_unit: DurationUnit;
  readonly est_duration_note: string;
  readonly est_daily_flight_h: number | null;
  readonly delivery_working_days: number | null;
  /** "the laws of the <State>" (TJR record 667). */
  readonly governing_law: string;
  /**
   * Datum a new boundary table starts with (the 2026 contract prints "Coordinates in WGS84"); each area's table keeps
   * its own (`QuoteArea.boundary_table.datum`, decision 170).
   */
  readonly coords_datum: string;
  readonly conduct_items: readonly ConductItem[];
  readonly fuel_by: FuelBy;
  readonly permits_by: PermitsBy;
  readonly free_grid_offer: boolean;
  readonly print_boundary_coords: boolean;
  /** Who signs the quote letter (name and title as printed). */
  readonly signatory_name: string;
  readonly signatory_title: string;
  readonly notes: string;
  readonly deleted_at: IsoDateTime | null;
}

/**
 * One option of a quote (decision 171): an alternative offered side by side, e.g. another block size or aircraft
 * type, with its own areas, charges and total. Every quote has at least one; the client chooses one.
 */
export interface QuoteOption {
  readonly id: Uuid;
  readonly quote_id: Uuid;
  readonly sort: number;
  /** "A", "B", … (printed "Option A"). */
  readonly label: string;
  /** What differs, e.g. "50 m line spacing" (printed after the label). */
  readonly description: string;
  /** Aircraft type of this option; '' = the quote's. */
  readonly aircraft_type: string;
  /** Cruise speed of that type in knots; null = the quote's. */
  readonly aircraft_cruise_kt: number | null;
  /**
   * A total line km typed or imported for this option (e.g. the total printed on a TJR quote or contract), kept only
   * to compare with the sum of its areas (decision 169); never printed.
   */
  readonly stated_total_line_km: number | null;
}

/**
 * The boundary coordinates as printed in the contract's section 2.2 (decision 170): the points as the client supplied
 * them, with their own datum, which may differ from the survey datum (F26048: printed in WGS 84, surveyed in GDA 94).
 */
export interface BoundaryTable {
  /** e.g. 'WGS84', 'GDA94', 'GDA2020'. */
  readonly datum: string;
  /** Grid: [easting, northing] in `zone`; geographic: [longitude, latitude] in decimal degrees. */
  readonly kind: 'grid' | 'geographic';
  readonly zone: number | null;
  readonly points: ReadonlyArray<readonly [number, number]>;
  /** The file the points came from. */
  readonly source: string;
}

export const BOUNDARY_DATUMS: ReadonlyArray<readonly [string, string]> = [
  ['WGS84', 'WGS 84'],
  ['GDA94', 'GDA 94'],
  ['GDA2020', 'GDA2020'],
];

/** One survey area ("block") of a quote: TJR record 730 row, filled from an imported lines file (decision 124). */
export interface QuoteArea {
  readonly id: Uuid;
  readonly quote_id: Uuid;
  /** The option the area belongs to (decision 171). */
  readonly option_id: Uuid;
  readonly sort: number;
  readonly name: string;
  readonly trav_spacing_m: number;
  readonly trav_direction_deg: number;
  readonly tie_spacing_m: number;
  readonly tie_direction_deg: number;
  /** Mean clearance / survey (sensor) height in metres (TJR `blkDatFM` Mean Clear). */
  readonly sensor_height_m: number;
  readonly zone: number | null;
  readonly hemisphere: Hemisphere;
  /** round(Σ traverse length / 1000), TJR's rounding (decision 124). */
  readonly traverse_km: number;
  readonly tie_km: number;
  /**
   * A line-km figure typed by hand or imported (an old quote or contract), with where it comes from. Decision 169:
   * when the area has a lines file the km are computed from it and this figure is only compared (and flagged when it
   * differs); it is used only for an area without a lines file.
   */
  readonly line_km_override: number | null;
  readonly override_reason: string;
  /** Dollars per line km (TJR "Cost/LK"). */
  readonly rate_per_line_km: number | null;
  /** Boundary vertices [easting, northing] in the area's zone (the project's block boundary). */
  readonly boundary: ReadonlyArray<readonly [number, number]>;
  /**
   * The printed boundary table with its own datum (decision 170). Null on older areas: the contract then prints
   * `boundary` in the quote's `coords_datum`.
   */
  readonly boundary_table: BoundaryTable | null;
  /** Name of the lines file the km were computed from (`*_LNS.lns`, or a `*_fnl.sum` with its line list); '' none. */
  readonly lines_file: string;
}

/** The commercial items MB kept (decision 126): no free-form extra charges. */
export type ChargeKind = 'mobilisation' | 'standby' | 'tasp' | 'data_merge' | 'fuel';
export const CHARGE_KINDS: readonly ChargeKind[] = ['mobilisation', 'standby', 'tasp', 'data_merge', 'fuel'];
export type FuelMode = 'none' | 'at_cost' | 'per_litre';

export interface QuoteCharge {
  readonly quote_id: Uuid;
  /** Each option has its own charges (decision 171): one row per option and kind. */
  readonly option_id: Uuid;
  readonly kind: ChargeKind;
  /** Dollars: once (mobilisation, TASP estimate, data merge), per day (standby), per litre (fuel). */
  readonly amount: number | null;
  /** Standby only: "Standby charges will not exceed N days". */
  readonly cap_days: number | null;
  /** Standby only: extra standby note (TJR record 669). */
  readonly note: string;
  /** Fuel only. */
  readonly fuel_mode: FuelMode;
}

/** Schedule of payments and payment terms (TJR Rates tab; contract 5.3–5.4). */
export interface CommercialTerms {
  readonly quote_id: Uuid;
  readonly pp1_pct: number | null;
  readonly pp2_pct: number | null;
  /** Paid on delivery of all final items; the three always total 100 % (`validatePaymentSchedule`). */
  readonly balance_pct: number | null;
  readonly payment_days: number;
  readonly late_fee_pct_month: number;
}

/** Sampling tab (TJR records 600, 712, 713, 716). */
export interface SamplingSpec {
  readonly quote_id: Uuid;
  readonly magnetometers: number;
  /** Null = magnetics not used. */
  readonly mag_rate_hz: number | null;
  readonly spectrometer_packs: number;
  /** One pack is 21 L (16.8 L down + 4.2 L up) instead of 16.8 L. */
  readonly upward_detector: boolean;
  /** Null = radiometrics not used. */
  readonly rad_rate_hz: number | null;
  /** Base station instrument as printed, e.g. "Geometrics G856AX proton precession magnetometer". */
  readonly base_station: string;
  /** Survey datum (TJR `datumCB`: WGS 84 or GDA 94). */
  readonly datum: string;
  readonly video: boolean;
  readonly ground_concentrations: boolean;
  readonly mag_located_format: string;
  readonly rad_located_format: string;
  readonly mag_grid_format: string;
  readonly rad_grid_format: string;
}

export type DeliverableKind = 'located' | 'grid';
/** One located data file or one gridded product list of section 3.4.2, items in printed order. */
export interface DeliverableSpec {
  readonly id: Uuid;
  readonly quote_id: Uuid;
  readonly sort: number;
  readonly kind: DeliverableKind;
  /** "Located Data File #1". */
  readonly label: string;
  /** "contains final magnetic, altitude and ancillary data". */
  readonly contents: string;
  /** Which method's sentence introduces the list: mag, rad or none (spectra files have no list). */
  readonly method: 'mag' | 'rad' | 'none';
  readonly items: readonly string[];
}

/** QC limits of a quote (the reflight specifications), version 1 until a contract exists. */
export interface QuoteQcSpec {
  readonly quote_id: Uuid;
  readonly rules: readonly QcRule[];
}

/** Everything of one quote revision: what "Send" freezes and "Make contract" copies (no re-keying, bug B5). */
export interface QuoteAggregate {
  readonly quote: Quote;
  /** At least one (decision 171); a contract's snapshot holds only the chosen option. */
  readonly options: readonly QuoteOption[];
  readonly client: ClientOrgDetails | null;
  readonly principal: ClientContact | null;
  readonly notices: ClientContact | null;
  readonly areas: readonly QuoteArea[];
  readonly charges: readonly QuoteCharge[];
  readonly terms: CommercialTerms;
  readonly sampling: SamplingSpec;
  readonly deliverables: readonly DeliverableSpec[];
  readonly qc: readonly QcRule[];
}

export interface QuoteRevision {
  readonly id: Uuid;
  readonly quote_id: Uuid;
  readonly revision: number;
  readonly status: 'sent' | 'accepted' | 'declined' | 'superseded';
  readonly snapshot: QuoteAggregate;
  readonly sent_at: IsoDateTime;
  /** The option the client chose when the revision was accepted (decision 171). */
  readonly chosen_option_id: Uuid | null;
}

// ---------------------------------------------------------------- contract

export type ContractStatus = 'draft' | 'issued' | 'signed' | 'closed';

export interface Contract {
  readonly id: Uuid;
  readonly tenant_id: Uuid;
  readonly quote_id: Uuid;
  readonly quote_revision: number;
  /** The option of that revision the contract is made from (decision 171). */
  readonly option_id: Uuid | null;
  /** Job number: the quote's digits with the job-type letter (F26048, decision 119). */
  readonly number: string;
  readonly status: ContractStatus;
  readonly contract_date: IsoDate;
  readonly template_version_id: Uuid | null;
  /** Every printed value, frozen: the accepted revision plus the clause sections in force. */
  readonly snapshot: ContractSnapshot;
  readonly signed_at: IsoDate | null;
  readonly signed_file_name: string;
  readonly project_id: Uuid | null;
}

export interface ContractSnapshot {
  readonly quote: QuoteAggregate;
  readonly clauses: ClauseTemplate;
}

export interface ContractVariation {
  readonly id: Uuid;
  readonly contract_id: Uuid;
  readonly number: number;
  readonly date: IsoDate;
  readonly summary: string;
  readonly reason: string;
  /** True when the client agreed the change (decision 122: then it is a contract variation in the full sense). */
  readonly client_agreed: boolean;
  readonly changes: Record<string, unknown>;
  readonly qc_spec_id: Uuid | null;
  /** Signed change of the contract's line km (decision 258); null when the variation does not change the km. */
  readonly line_km_change?: number | null;
}

// ---------------------------------------------------------------- tenant settings

/** Legal identity, notices address, tax setting (decision 125) and document footer of the tenant. */
export interface TenantProfile {
  readonly tenant_id: Uuid;
  readonly legal_name: string;
  /** Short defined term in clauses, e.g. "Thomson". */
  readonly short_name: string;
  readonly abn: string;
  readonly incorporated_in: string;
  readonly registered_office: string;
  readonly notices_address: readonly string[];
  readonly notices_attention: string;
  readonly notices_email: string;
  readonly footer: string;
  /** Off for Thomson Airborne (decision 125). */
  readonly tax_line_enabled: boolean;
  readonly tax_label: string;
  readonly tax_rate_pct: number;
}

/** Protected payment details (decision 127): tenant admin edits; managers and office staff read. */
export interface TenantPaymentDetails {
  readonly tenant_id: Uuid;
  readonly payee_address: readonly string[];
  readonly account_name: string;
  readonly bank_name: string;
  readonly bank_address: string;
  readonly bsb: string;
  readonly account_number: string;
  readonly swift: string;
}

// ---------------------------------------------------------------- clause templates

/** A condition on a section or block: `mag`, `rad`, `video`, `!op`, `pricing`, … (`clauseCondition`). */
export type ClauseWhen = string | null;

export type ClauseBlock =
  | { readonly p: string; readonly when?: ClauseWhen }
  | { readonly bullets: readonly string[]; readonly when?: ClauseWhen }
  | { readonly label: string; readonly when?: ClauseWhen }
  | { readonly kv: ReadonlyArray<readonly [string, string]>; readonly when?: ClauseWhen }
  | { readonly generated: GeneratedBlock; readonly when?: ClauseWhen };

/** Per-job blocks the document builder fills (`contract-document.ts`). */
export type GeneratedBlock =
  | 'party_sentence'
  | 'conduct_items'
  | 'survey_specs'
  | 'area_coordinates'
  | 'principal_contact'
  | 'equipment_mag'
  | 'equipment_rad'
  | 'equipment_bullets'
  | 'reflight_rules'
  | 'located_files'
  | 'charges'
  | 'standby_cap'
  | 'payment_schedule'
  | 'payee'
  | 'bank_account'
  | 'notices_addresses'
  | 'quote_areas'
  | 'free_grid_offer'
  | 'signing';

export interface ClauseSection {
  /** Stable id, used for cross-references `{{ref:<id>}}` (bug B6). */
  readonly id: string;
  /** 1 = heading (numbered 1, 2, …), 2 = topic (2.1), 3 = sub-topic (2.8.1); 0 = unnumbered. */
  readonly level: 0 | 1 | 2 | 3;
  readonly heading: string;
  readonly when?: ClauseWhen;
  readonly blocks: readonly ClauseBlock[];
}

export interface ClauseTemplate {
  readonly kind: 'quote' | 'contract';
  /** e.g. "TJR 2026-10". */
  readonly version: string;
  readonly source: string;
  /** Tenant wording parameters, e.g. `late_fee_pct_month`, `industry_better_pct`. */
  readonly params: Readonly<Record<string, string | number>>;
  /** Item wording, e.g. `conduct.meals`, `reflight.flight_path`. */
  readonly phrases: Readonly<Record<string, string>>;
  readonly sections: readonly ClauseSection[];
  /** Fixed lists, e.g. `free_grid_offer`. */
  readonly lists?: Readonly<Record<string, readonly string[]>>;
}
